/**
 * Where a tile lives inside a tileset image.
 *
 * ONE COPY OF THE ARITHMETIC, because three things now need it: the game
 * renderer (`TileMap`), the editor canvas, and the editor's tile picker. Two of
 * those draw the same world at different zoom levels, so a disagreement here
 * would show up as the editor and the game rendering different scenery from
 * identical data — which reads as a save that did not take.
 *
 * GIDS ARE 1-BASED. Tiled reserves 0 for "no tile", and `firstgid` is the id of
 * the sheet's first tile, so the row and column come from `gid - firstgid`.
 */

/** Tiled's "no tile here". */
export const EMPTY_GID = 0;

/**
 * The source rectangle for `gid`, or null if there is nothing to draw.
 *
 * Returns null rather than a zero-size rect for the empty tile: callers skip on
 * null, and a rect of width 0 would be a silent no-op that looks like a
 * successful draw.
 *
 * @param {number} gid
 * @param {{columns: number, firstgid?: number, tileSize: number}} sheet
 * @returns {{sx: number, sy: number, size: number} | null}
 */
export function tileSourceRect(gid, { columns, firstgid = 1, tileSize }) {
  if (!gid || gid === EMPTY_GID) return null;

  const local = gid - firstgid;
  // A gid below `firstgid` belongs to a different tileset, and one past the end
  // of the sheet would sample transparent pixels off the bottom. Both mean the
  // map and the art disagree; drawing nothing is the honest answer.
  if (local < 0 || !columns) return null;

  return {
    sx: (local % columns) * tileSize,
    sy: Math.floor(local / columns) * tileSize,
    size: tileSize,
  };
}

/**
 * Look up what a tile is called, from the manifest the generator writes.
 *
 * OPTIONAL BY DESIGN. A downloaded tileset has no manifest, and the picker
 * still has to work — it just shows numbers. So this never throws and never
 * requires the file to exist.
 *
 * @param {{tiles?: {gid: number, name: string, role: string}[]} | null} manifest
 * @returns {Map<number, {gid: number, name: string, role: string}>}
 */
export function tilesByGid(manifest) {
  return new Map((manifest?.tiles || []).map((t) => [t.gid, t]));
}

export default tileSourceRect;
